import { z } from 'zod';

import type { ToolExecutionContext } from '../types';

import { resolveSceneHandle } from './scene-addressing';
import { SetTextTool } from './SetTextTool';
import { VideoStateTool } from './VideoStateTool';

import type { RootState } from 'js/src/libs/RootState';
import { isVoiceDrivenSceneGroup } from 'js/src/luminary';
import { makeOperationsCallable } from 'js/src/luminary/luminary-hooks';
import { addNewSubsceneOperation } from 'js/src/luminary/luminary-operations';

export const addSubsceneSchema = z.object({
    scene_id: z.string().describe('The scene_id from the video state.'),
    subscene_index: z
        .number()
        .optional()
        .describe(
            'Optional position to insert the new subscene at (0-based). If not provided, the subscene is appended after the last existing subscene.',
        ),
});
export type AddSubsceneArgs = z.infer<typeof addSubsceneSchema>;

export class AddSubsceneTool extends VideoStateTool<typeof addSubsceneSchema> {
    public static readonly toolName = 'add_subscene';

    protected override readonly schema = addSubsceneSchema;
    protected override readonly toolName = AddSubsceneTool.toolName;
    protected override readonly toolDescription = `Add a subscene to a scene in a text video. A subscene is a step within a single scene: it keeps the scene's visual content (media, background, layout) on screen while animating one set of text out and the next set in. The most common use is splitting a longer sentence across two subscenes at a natural break point, so each step shows only a small, snappy amount of text while the underlying visuals stay continuous. The new subscene is created as a copy of the current last subscene, so update its text afterward with ${SetTextTool.toolName} (target it via its subscene_index). Only valid for text scenes (no voiceover) — voice-driven videos have their timing controlled by the audio and do not support subscenes.`;

    public override getToolDisplayName(): string {
        return 'Adding subscene';
    }

    public get statusIcon(): string {
        return 'add_photo_alternate';
    }

    public async execute(args: AddSubsceneArgs, context: ToolExecutionContext) {
        const { subscene_index } = args;
        const { parentActionGroup } = context;

        try {
            const { sceneIndex: scene_index, sceneShortId } = resolveSceneHandle(
                this.luminary,
                this.getState(),
                args,
            );
            const scene = this.luminary.scenes[scene_index];

            const sceneGroup = scene.sceneGroup;
            if (!sceneGroup) {
                return `Error: No scene group found for scene ${sceneShortId}`;
            }

            // Subscenes are only supported on text scenes. Voice-driven scene
            // groups (voiceover, talking head, avatar) have their timing driven
            // by the audio and cannot have subscenes added.
            if (isVoiceDrivenSceneGroup(sceneGroup)) {
                return `Error: Cannot add a subscene to scene ${sceneShortId} because it belongs to a voice-driven video. Subscenes are only supported on text videos (no voiceover).`;
            }

            const insertIndex =
                typeof subscene_index === 'number' ? subscene_index : scene.subscenes.length;

            if (insertIndex < 0 || insertIndex > scene.subscenes.length) {
                return `Error: subscene_index ${insertIndex} is out of bounds. Scene ${sceneShortId} has ${scene.subscenes.length} subscene(s), so a new subscene must be inserted between 0 and ${scene.subscenes.length}`;
            }

            const { addNewSubscene } = makeOperationsCallable(
                {
                    addNewSubsceneOperation,
                },
                this.luminary,
                this.dispatch,
                () => null as unknown as RootState, // NOTE: no state currently
            );

            try {
                addNewSubscene(scene.id, insertIndex, false, parentActionGroup);

                return `Success: Added subscene at index ${insertIndex} to scene ${sceneShortId}. Scene ${sceneShortId} now has ${scene.subscenes.length} subscenes. Update the new subscene's text with ${SetTextTool.toolName}.`;
            } catch (error) {
                const errorMessage =
                    error instanceof Error ? error.message : 'Unknown error occurred';
                return `Error adding subscene: ${errorMessage}`;
            }
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
            return `Error adding subscene: ${errorMessage}`;
        }
    }
}
